import { signalInApp } from '../../../inApp/signals';
import LoadFailure from '../../../components/Brand/LoadFailure';
import { appLocalStorage } from '../../../utils/storage';
import { BackIcon, TrashIcon, PhotoIcon, KeyboardHideIcon, RedoIcon, UndoIcon, SendIcon, MicIcon, VideoCameraIcon } from '../../../components/Brand/Icon';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import useViewport from '../../../hooks/useViewport';
import { waitForFeedVotes } from '../../../hooks/useFeedVote';
import PostUser from '../../../components/PostUser';
import UserImage from '../../../components/UserImage';
import UserReactionScope from '../../../components/UserReactionScope';
import Voting from '../../../components/WiwFooter/Voting';
import { useEditor, EditorContent } from '@tiptap/react';
import { useTranslation } from 'react-i18next';
import StarterKit from '@tiptap/starter-kit';
import Image, { DrawingContext, insertDrawing, replaceDrawing } from '../../../drawing/ImageExtension';
import ImageMenu from '../../../drawing/ImageMenu';
import DrawingDialog from '../../../drawing/DrawingDialog';
import { drawingScope, clearDrawings } from '../../../drawing/store';
import { serializeDrawingHTML } from '../../../drawing/model';
import ExternalLinkExtension from './ExternalLinkExtension';
import RecordedMediaExtension, { MediaDraftContext, MEDIA_SYNC, MEDIA_DELETE, recordedMediaKeys, serializeRecordedMedia } from './RecordedMediaExtension';
import StoryTitle from '../../../components/StoryTitle';
import ConfirmationPanel from '../../../components/ConfirmationPanel';
import { TextSelection } from 'prosemirror-state';
import { Button } from 'antd';
import message from '../../../components/Brand/brandMessage';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import url from '../../../utils/url';
import { opinionPath } from '../../../utils/opinionPath';
import api from '../../../services';
import { getUser, handleImageUpload, getPrivatFlg, setLocalStorage } from '../../../utils/func';
import { IMAGE_PICKER_ACCEPT } from '../../../utils/imageUpload';
import { seedCensoredSpacing } from './censoredSpacing';
import { APP_ROUTES } from '../../../utils/constant';
import BrandLoader from '../../../components/Brand/BrandLoader';
import ContentLimitStatus from '../../../components/Brand/ContentLimitStatus';
import { CONTENT_LIMITS, measureComment, contentLimitMessage } from '../../../utils/contentLimits';
import { accountProtectionMessage } from '../../../accountProtection/feedback';
import QuoteExtension, { QuoteEditorContext } from './QuoteExtension';
import { hasQuotePlacement, quotePlacementHTML } from '../../../components/ReactionQuote/placement';
import { createOpinionDraft, initialOpinionHTML, resolveOpinionDraft, updateOpinionDraft } from './opinionDraft';
import { clearQuoteDraft } from './quoteDraft';
import useQuoteAttachment from './useQuoteAttachment';
import useEditorViewport, { scrollEditorSelectionIntoView } from './editorViewport';
import { linkifyExternalHTML } from '../../../utils/externalLinkText';
import MediaRecorderPanel from '../../../components/MediaRecorder';
import useMediaCapabilities from '../../../media/useMediaCapabilities';
import useMediaDraft from '../../../media/useMediaDraft';
import { clearDraftIdentity } from '../../../media/draftIdentity';
import { completeGuestHandoff, getGuestTrialSnapshot, prepareGuestAuth, recordGuestVote } from '../../../guestTrial/store';
import { useGuestTrialNotice } from '../../../guestTrial/useGuestTrialNotice';
import { recordingSupported, RECORDING_LIMITS } from '../../../media/recorder';
import { stopAllPlayers } from '../../../components/ExternalLink/playerState';
import './ReactionEditor.scss';

const legacyMediaPosition = doc => {
  let position = doc.content.size;
  doc.descendants((node, pos) => { if (node.type.name === 'reactionQuote') position = Math.min(position, pos); });
  return position;
};

const { reactionSaveVoting, storyComment, storyLoad, reactionDeleteById } = api;

const clearStoredOpinion = (draft, userId, storyId) => {
  // The server has already accepted the action. A browser storage failure must
  // not turn it into a failed save/delete or invite a duplicate submission.
  [draft.key, `comment-guest-import:${userId}:${storyId}`].forEach(key => {
    try { appLocalStorage.removeItem(key); } catch { /* Local cleanup is best effort. */ }
  });
};

const ReactionEditor = ({ scope = 4 }) => {
  const { t } = useTranslation();
  const storageUser = getUser();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const userId = storageUser?.id;
  const guest = !userId;
  const { showNotice: showGuestNotice } = useGuestTrialNotice(params.id, requestGuestSignIn);
  const editorPath = (location.pathname || window.location.pathname) + (location.search || '') + (location.hash || '');
  const recoveredScope = location.state?.protectionRestoreScore;
  const intentScore = location.state?.opinionIntent?.score;
  const intentPrivacy = location.state?.opinionIntent?.privat;
  const draft = useMemo(() => {
    const next = createOpinionDraft(userId, params.id, editorPath);
    if (Number.isInteger(recoveredScope) && recoveredScope >= 1 && recoveredScope <= 7) next.score = recoveredScope;
    else if (Number.isInteger(intentScore) && intentScore >= 1 && intentScore <= 7) next.score = intentScore;
    return next;
  }, [userId, params.id, editorPath, recoveredScope, intentScore]);
  const previewStory = Number(location.state?.story?.id) === Number(params.id) ? location.state.story : null;
  const viewport = useViewport();
  const keyboardVisible = viewport.keyboardOpen;
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [storyId, setStoryId] = useState(null);
  const [storyTitle, setStoryTitle] = useState(previewStory?.title ?? null);
  const [reactionId, setReactionId] = useState(null);
  const [savedQuote, setSavedQuote] = useState(null);
  const [savedMedia, setSavedMedia] = useState(null);
  const [mediaConflict, setMediaConflict] = useState(false);
  const [recordingState, setRecording] = useState(null);
  const recording = recordingState?.draftId === draft.id ? recordingState : null;
  const recordingBack = useRef(() => {});
  const mediaBaseline = useRef([]);
  const caps = useMediaCapabilities(userId);
  const media = useMediaDraft({ userId, storyId: params.id, draftId: draft.id, savedMedia, caps });
  const { items: inlineMediaItems, ready: inlineMediaReady, syncMedia: syncInlineMedia } = media;
  const toolbarCaps = recording?.caps || caps;
  const newMediaToolbar = toolbarCaps.audio.enabled || toolbarCaps.video.enabled;
  const canRecord = recordingSupported();
  const [addScope, setAddScope] = useState(draft.score ?? scope);
  const [guestStorageError, setGuestStorageError] = useState(false);
  const [showControl, setShowControl] = useState(false);
  const [mediaRemoval, setMediaRemoval] = useState(null);
  const mediaRuntime = useRef(null);
  const requestMediaRemoval = useRef(null);
  requestMediaRemoval.current = request => setMediaRemoval({ ...request, draftId: draft.id });
  const recordedMediaExtension = useMemo(() => RecordedMediaExtension.configure({
    onDeleteRequest: request => requestMediaRemoval.current(request),
  }), []);
  mediaRuntime.current = { media, draft };
  const mounted = useRef(false);
  const pending = useRef(false);
  const pageVersion = useRef(0);
  const opinionPrivacy = useRef(false);
  const attachment = useQuoteAttachment({ userId, storyId: params.id, location, reactionId, savedQuote });
  const initialHtml = useMemo(() => {
    const withQuote = Boolean((attachment.draft.id || attachment.draft.invalid) && !attachment.draft.placementRemoved);
    const html = initialOpinionHTML(draft, withQuote);
    return linkifyExternalHTML(withQuote || attachment.draft.placementRemoved ? quotePlacementHTML(html, withQuote) : html);
  }, [draft, attachment.draft]);
  const [contentMeasurement, setContentMeasurement] = useState(() => measureComment(initialHtml));
  const measurementTimer = useRef(null);
  const refreshMeasurement = html => {
    clearTimeout(measurementTimer.current);
    // Base64 photos can make HTML several MiB long. Delay the display-only
    // count until a typing pause; Send always validates the current HTML below.
    if (html.length > 256 * 1024) measurementTimer.current = setTimeout(() => {
      if (mounted.current) setContentMeasurement(measureComment(html));
    }, 180);
    else setContentMeasurement(measureComment(html));
  };
  const [imageMenuState, setImageMenu] = useState(null);
  const imageMenu = imageMenuState?.draftId === draft.id ? imageMenuState : null;
  const [drawingState, setDrawing] = useState(null);
  const drawingFocus = useRef(null);
  const drawing = drawingState?.draftId === draft.id ? drawingState : null;
  const drawingsScope = drawingScope(userId, params.id, draft.id);
  const imageSelection = useRef(null);
  const imageInput = useRef(null);
  const imageRequest = useRef(null);
  const imageController = useRef(null);
  const busy = initializing || Boolean(loading);
  const saveBlocked = busy || (!guest && ((!attachment.draft.placementRemoved && (attachment.pending || attachment.error)) || media.pending || mediaConflict)) || Boolean(recording) || Boolean(mediaRemoval) || showControl || Boolean(drawing) || Boolean(imageMenu);
  const loginPath = url(`${APP_ROUTES.LOGIN}?from=${encodeURIComponent(
    (location.pathname || window.location.pathname) + (location.search || '') + (location.hash || '')
  )}`);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; clearTimeout(measurementTimer.current); };
  }, []);

  useEffect(() => {
    if (guest) showGuestNotice();
  }, [guest, params.id, showGuestNotice]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      ...(!guest ? [Image.configure({ allowBase64: true })] : []),
      ExternalLinkExtension,
      ...(!guest ? [recordedMediaExtension, QuoteExtension] : []),
    ],
    content: initialHtml,
    editorProps: { handleScrollToSelection: scrollEditorSelectionIntoView },
    // Bind callbacks to the document that owns this editor instance, including
    // the render before useEditor's route-change effect replaces the old one.
    opinionDraft: draft,
    onFocus: ({ editor }) => {
      if (editor.options.opinionDraft !== draft || draft.focused) return;
      draft.focused = true;
      if (location.state?.focusComposer) {
        // Start a fresh repost on its first editable line. Recovered text keeps
        // the usual end position, and async hydration maps it as before.
        const freshRepost = !draft.hasFullDraft && !draft.changed && attachment.draft.id
          && editor.getHTML() === initialHtml;
        editor.commands.setTextSelection((freshRepost
          ? TextSelection.atStart(editor.state.doc)
          : TextSelection.atEnd(editor.state.doc)).from);
      }
    },
    onUpdate: ({ editor, transaction }) => {
      if (!mounted.current) return;
      if (editor.options.opinionDraft !== draft) return;
      const html = editor.getHTML();
      refreshMeasurement(html);
      if (!guest && draft.resolved && editor.state?.doc && !transaction?.getMeta(MEDIA_SYNC)) {
        mediaRuntime.current.media.syncMedia(recordedMediaKeys(editor.state.doc));
      }
      if (!guest && !draft.merging && (draft.resolved || initialHtml.includes('data-wiw-quote'))) {
        attachment.setPlacement(hasQuotePlacement(editor.state.doc));
      }
      updateOpinionDraft(draft, html);
      if (guest || draft.storageError || guestStorageError) setGuestStorageError(Boolean(draft.storageError));
    },
  }, [params.id, userId]);

  useEditorViewport(editor, viewport);

  useEffect(() => {
    if (!editor) return;
    const trackSelection = ({ transaction }) => {
      if (transaction.docChanged && imageSelection.current) {
        imageSelection.current = imageSelection.current.map(transaction.mapping);
      }
    };
    editor.on('transaction', trackSelection);
    return () => { editor.off('transaction', trackSelection); imageSelection.current = null; };
  }, [editor]);

  useEffect(() => {
    if (guest && (new URLSearchParams(location.search || '').has('quote') || location.state?.quote)) {
      navigate(loginPath, { replace: true });
      return;
    }
    if (!editor || editor.options.opinionDraft !== draft) return;

    let active = true;
    const controller = new AbortController();
    let cancelCompositionWait = () => {};
    pageVersion.current += 1;
    pending.current = false;
    setLoading(false);
    setInitializing(true);
    setLoadError(false);
    setStoryId(null);
    setReactionId(null);
    setSavedQuote(null);
    setSavedMedia(null);
    setMediaConflict(false);
    setRecording(null);
    mediaBaseline.current = [];
    setShowControl(false);
    setMediaRemoval(null);
    setAddScope(draft.score ?? scope);
    setGuestStorageError(Boolean(draft.storageError));
    clearTimeout(measurementTimer.current);
    setContentMeasurement(measureComment(editor.getHTML()));

    const waitForComposition = () => new Promise(resolve => {
      const element = editor.view.dom;
      let timer;
      const finish = () => {
        element.removeEventListener('compositionend', onEnd);
        clearTimeout(timer);
        resolve();
      };
      const onEnd = () => { timer = setTimeout(finish, 0); };
      cancelCompositionWait = finish;
      element.addEventListener('compositionend', onEnd, { once: true });
    });

    const init = async () => {
      try {
        const id = Number(params.id);
        if (!Number.isSafeInteger(id) || id <= 0) throw new Error('Invalid reaction');
        await waitForFeedVotes(id, controller.signal);
        if (!active || editor.isDestroyed) return;
        const response = guest ? await storyLoad({ id }) : await storyComment({ id });
        const reaction = guest ? { ...response, story: response, id: null } : response;
        if (!active || editor.isDestroyed) return;
        if (reaction.statusCode === 401) {
          navigate(loginPath, { replace: true });
          return;
        }
        if (!reaction.success || Number(reaction.story?.id) !== id) throw new Error('Reaction unavailable');
        // IME owns the composing DOM. Wait until its final input has reached
        // ProseMirror before prepending a remotely restored opinion.
        while (editor.view.composing) {
          await waitForComposition();
          if (!active || editor.isDestroyed) return;
        }
        setStoryId(reaction.story.id);
        setReactionId(reaction.id);
        setSavedQuote(userId === reaction.user?.id
          ? reaction.quote || (reaction.quoted_reaction_id ? { id: reaction.quoted_reaction_id, unavailable: true } : null)
          : null);
        const ownMedia = userId === reaction.user?.id && Array.isArray(reaction.media) ? reaction.media : [];
        setSavedMedia(ownMedia);
        mediaBaseline.current = ownMedia.map(item => item.id);
        setStoryTitle(reaction.story.title);
        if (reaction.scope && draft.score === undefined) setAddScope(reaction.scope);
        // Editing keeps this opinion's saved visibility. The last slider choice
        // belongs to new opinions and must not silently publish a hidden one.
        opinionPrivacy.current = typeof intentPrivacy === 'boolean' ? intentPrivacy
          : userId === reaction.user?.id && typeof reaction.privat === 'boolean'
          ? reaction.privat : getPrivatFlg();
        const savedText = userId === reaction.user?.id ? reaction.text_msg || '' : '';
        // Older recordings can live outside HTML. They are existing content,
        // so replace untouched starter lines before restoring their inline nodes.
        const restoredText = savedText || (ownMedia.length && !draft.changed ? '<p></p>' : '');
        resolveOpinionDraft(draft, editor, linkifyExternalHTML(restoredText));
        setGuestStorageError(Boolean(draft.storageError));
        // Hydrate placement only once, after the saved relation is known. Never
        // reinsert a quote the author removed with the keyboard or close button.
        const withQuote = !attachment.draft.placementRemoved && (attachment.draft.id !== undefined
          ? Boolean(attachment.draft.id || attachment.draft.invalid)
          : Boolean(userId === reaction.user?.id && (reaction.quote || reaction.quoted_reaction_id)));
        const placedHTML = quotePlacementHTML(editor.getHTML(), withQuote);
        if (placedHTML !== editor.getHTML()) {
          draft.merging = true;
          try {
            // Appending the legacy quote leaves the existing caret/history intact.
            if (withQuote && !hasQuotePlacement(editor.state.doc)) {
              editor.chain().setMeta('addToHistory', false).insertContentAt(editor.state.doc.content.size,
                '<div data-wiw-quote="true"></div><p></p>', { updateSelection: false }).run();
            } else editor.chain().setMeta('addToHistory', false).setContent(placedHTML).run();
          } finally { draft.merging = false; }
          draft.html = editor.getHTML();
        }
        clearTimeout(measurementTimer.current);
        setContentMeasurement(measureComment(editor.getHTML()));
      } catch (error) {
        if (active) setLoadError(true);
      } finally {
        if (active) setInitializing(false);
      }
    };

    init();
    return () => {
      active = false;
      controller.abort();
      pageVersion.current += 1;
      imageRequest.current = null;
      imageController.current?.abort();
      cancelCompositionWait();
    };
  }, [editor, draft, attachment.draft, params.id, userId, guest, navigate, loadAttempt, loginPath, location.search, location.state?.quote, scope, intentPrivacy]);

  useEffect(() => {
    // Metadata loading must not interrupt focus, typing or an IME composition.
    // Save/delete/image operations still lock the document as before.
    if (editor && !editor.isDestroyed) editor.setEditable(!loading && !mediaRemoval && !showControl && !drawing && !imageMenu, false);
    if (editor && !editor.isDestroyed && drawingFocus.current === editor && !loading && !drawing && !imageMenu && !mediaRemoval && !showControl) {
      drawingFocus.current = null;
      editor.view.focus();
    }
  }, [editor, loading, mediaRemoval, showControl, drawing, imageMenu]);

  useEffect(() => {
    if (guest || !editor?.state?.doc || editor.isDestroyed || initializing || !inlineMediaReady || mediaRemoval || showControl) return;
    const existing = new Set(recordedMediaKeys(editor.state.doc));
    const missing = inlineMediaItems.filter(item => !existing.has(item.localId) && !existing.has(item.id));
    // Old comments and recovered drafts had media after HTML. Upgrade those
    // placements once, without touching the live caret or text history.
    if (missing.length) {
      const chain = editor.chain().setMeta('addToHistory', false);
      missing.forEach(item => chain.command(({ tr, commands }) => commands.insertRecordedMedia(item.id || item.localId,
        { at: legacyMediaPosition(tr.doc), updateSelection: false })));
      chain.run();
    }
    seedCensoredSpacing(editor, inlineMediaItems);
    // Recover a text/IndexedDB write interrupted between the two stores, and
    // reconcile any recording nodes restored by the text Undo history.
    syncInlineMedia(recordedMediaKeys(editor.state.doc));
    if (editor.view.composing) {
      const element = editor.view.dom;
      let timer;
      const afterComposition = () => {
        timer = setTimeout(() => seedCensoredSpacing(editor, inlineMediaItems), 0);
      };
      element.addEventListener('compositionend', afterComposition, { once: true });
      return () => { clearTimeout(timer); element.removeEventListener('compositionend', afterComposition); };
    }
  }, [editor, guest, initializing, inlineMediaReady, inlineMediaItems, syncInlineMedia, mediaRemoval, showControl]);

  function requestGuestSignIn() {
    if (!editor || editor.isDestroyed) return;
    draft.score = addScope;
    updateOpinionDraft(draft, editor.getHTML());
    setGuestStorageError(Boolean(draft.storageError));
    if (draft.storageError) return;
    const from = prepareGuestAuth(params.id, editorPath);
    if (!getGuestTrialSnapshot().storageAvailable) { setGuestStorageError(true); return; }
    navigate(`${url(APP_ROUTES.LOGIN)}?from=${encodeURIComponent(from)}`);
  }

  const canInsertImage = () => {
    if (saveBlocked || !editor || editor.isDestroyed || !storyId) return false;
    if (guest) { requestGuestSignIn(); return false; }
    if (measureComment(editor.getHTML()).photos >= CONTENT_LIMITS.photos) {
      message.error({ content: contentLimitMessage('COMMENT_PHOTO_LIMIT_EXCEEDED', t) });
      return false;
    }
    return true;
  };
  const openImageMenu = event => {
    if (!canInsertImage()) return;
    imageSelection.current = editor.state.selection?.getBookmark();
    editor.commands.blur?.();
    setImageMenu({ anchor: event.currentTarget, draftId: draft.id });
  };
  const openDrawing = id => {
    if (busy || recording || mediaRemoval || showControl || guest || !editor || !storyId) return;
    if (!id && measureComment(editor.getHTML()).photos >= CONTENT_LIMITS.photos) return;
    stopAllPlayers();
    editor.commands.blur?.();
    setImageMenu(null);
    setDrawing({ id, userId, scope: drawingsScope, draftId: draft.id, version: pageVersion.current });
  };
  const addDrawing = ({ src, id, persisted }) => {
    if (!mounted.current || drawing?.version !== pageVersion.current || editor.isDestroyed || getUser()?.id !== userId) {
      throw new Error('STALE_DRAWING');
    }
    if (drawing.id) {
      if (!replaceDrawing(editor, drawing.id, src)) throw new Error('DRAWING_REMOVED');
    } else {
      if (measureComment(editor.getHTML()).photos >= CONTENT_LIMITS.photos) throw new Error('PHOTO_LIMIT');
      insertDrawing(editor, src, id, imageSelection.current);
    }
    drawingFocus.current = editor;
    setDrawing(null);
    if (!persisted) message.error({ content: t('drawing:storageError') });
  };

  const handleInsertImage = () => {
    if (busy || pending.current || !editor || editor.isDestroyed || !storyId) return;
    if (guest) { requestGuestSignIn(); return; }
    if (measureComment(editor.getHTML()).photos >= CONTENT_LIMITS.photos) {
      message.error({ content: contentLimitMessage('COMMENT_PHOTO_LIMIT_EXCEEDED', t) });
      return;
    }
    imageRequest.current = { version: pageVersion.current, userId, editor };
    // Keep the input mounted and outside the inert menu background. Open it
    // synchronously from the tap so iOS retains the user activation.
    const input = imageInput.current;
    input.value = '';
    try { input.click(); }
    catch { message.error({ content: t('brand:imageError') }); imageRequest.current = null; }
    setImageMenu(null);
  };

  const imagePicked = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const request = imageRequest.current;
    imageRequest.current = null;
    const current = () => mounted.current && request?.version === pageVersion.current
      && getUser()?.id === request.userId && !request.editor.isDestroyed;
    if (!file || !current() || pending.current) return;
    const controller = new AbortController();
    imageController.current = controller;
    pending.current = true;
    setLoading('image');
    try {
      const base = await handleImageUpload(file, { signal: controller.signal });
      if (!current()) return;
      if (!base?.data?.link) throw new Error('Image unavailable');
      insertDrawing(request.editor, base.data.link, null, imageSelection.current);
      // Restore focus after the loading effect makes the editor editable.
      drawingFocus.current = request.editor;
    } catch (error) {
      if (current() && !controller.signal.aborted) {
        message.error({ content: t(error?.code === 'IMAGE_UNREADABLE' ? 'brand:imageFormatError' : 'brand:imageError') });
      }
    } finally {
      if (imageController.current === controller) imageController.current = null;
      if (mounted.current && request.version === pageVersion.current) {
        pending.current = false;
        setLoading(false);
      }
    }
  };

  const handleDelete = async () => {
    if (pending.current || busy || !editor || !storyId) return;
    if (!reactionId) {
      editor.chain().setMeta(MEDIA_DELETE, true).setContent('').run();
      if (guest) updateOpinionDraft(draft, '');
      else {
        setLocalStorage(draft.key, '');
        attachment.detach();
        media.removeAll();
      }
      setShowControl(false);
      return;
    }
    pending.current = true;
    setLoading('delete');
    const version = pageVersion.current;
    let leaving = false;
    try {
      const response = await reactionDeleteById({ id: reactionId });
      if (!mounted.current || version !== pageVersion.current) return;
      if (response.cancelled) return;
      if (response.statusCode === 401) {
        leaving = true;
        navigate(loginPath, { replace: true });
        return;
      }
      if (!response.success) throw new Error('Delete failed');
      clearStoredOpinion(draft, userId, params.id);
      if (draft.handoff) completeGuestHandoff(draft.handoff.id);
      clearQuoteDraft(attachment.draft);
      void clearDrawings(drawingsScope).catch(() => {});
      await media.clear();
      clearDraftIdentity(userId, params.id);
      if (!mounted.current || version !== pageVersion.current) return;
      message.success({ content: t('reaction.delete') });
      leaving = true;
      navigate(url('/'));
    } catch (error) {
      if (mounted.current && version === pageVersion.current) {
        message.error({ content: t('brand:deleteError', { defaultValue: 'Could not delete this opinion. Please try again.' }) });
      }
    } finally {
      if (mounted.current && version === pageVersion.current && !leaving) {
        pending.current = false;
        setLoading(false);
      }
    }
  };

  const hideKeyboard = () => {
    if (editor) {
      editor.commands.blur();
    }
  };

  const changeVotingComplete = (scope) => {
    if (!busy && !pending.current) {
      if (guest) {
        const result = recordGuestVote(params.id, scope);
        showGuestNotice(result);
        if (!result.accepted) { setAddScope(result.opinion?.score ?? draft.score ?? 4); return; }
      }
      setAddScope(scope);
      draft.score = scope;
      if (guest && editor) {
        updateOpinionDraft(draft, editor.getHTML());
        setGuestStorageError(Boolean(draft.storageError));
      }
    }
  };

  const onBack = () => {
    if (mediaRemoval) finishMediaRemoval(false);
    else if (showControl) setShowControl(false);
    else if (recording) recordingBack.current();
    else navigate(-1);
  };

  const openRecorder = kind => {
    if (busy || recording || !canRecord || !caps[kind]?.enabled || !media.ready) return;
    stopAllPlayers();
    hideKeyboard();
    setRecording({ kind, draftId: draft.id, caps });
  };

  const sendVoting = async () => {
    if (pending.current || saveBlocked || !editor || !storyId) return;
    const measurement = measureComment(editor.getHTML());
    setContentMeasurement(measurement);
    if (measurement.error) { message.error({ content: contentLimitMessage(measurement.error, t) }); return; }
    // A different tab may have installed another account while this document
    // was open. Re-enter the route before reading or sending its account draft.
    if (getUser()?.id !== userId) { navigate(editorPath, { replace: true }); return; }
    if (guest) { requestGuestSignIn(); return; }
    pending.current = true;
    setLoading('save');
    const version = pageVersion.current;
    let leaving = false;
    try {
      const response = await reactionSaveVoting({ story_id: Number(storyId), parent_reaction: reactionId,
        // Editable blank lines alone are not a text comment. Keep the existing
        // API representation of an empty document; media and quotes stay separate.
        score: addScope, txt_message: serializeDrawingHTML(serializeRecordedMedia(editor.isEmpty ? '<p></p>' : editor.getHTML(), media.items)), privat: opinionPrivacy.current,
        ...(!hasQuotePlacement(editor.state?.doc) && (attachment.changed || savedQuote) ? { quoted_reaction_id: null }
          : attachment.changed ? { quoted_reaction_id: attachment.draft.id } : {}),
        ...(media.changed ? { media_ids: media.mediaIds, media_base_ids: media.baseline || mediaBaseline.current } : {}) });
      if (!mounted.current || version !== pageVersion.current) return;
      if (response.statusCode === 401) {
        leaving = true;
        navigate(loginPath, { replace: true });
        return;
      }
      if (!response.success) {
        if (response.message === 'MEDIA_EDIT_CONFLICT') setMediaConflict(true);
        const quotesUnavailable = attachment.changed && attachment.draft.id && response.statusCode === 503
          && response.message === 'Reposts are not available yet. Please try again later.';
        const linkBlocked = response.statusCode === 422 && response.message === 'LINK_BLOCKED';
        const linkCheckUnavailable = response.statusCode === 503 && response.message === 'LINK_CHECK_UNAVAILABLE';
        const linkLimitExceeded = response.statusCode === 422 && response.message === 'LINK_LIMIT_EXCEEDED';
        const mediaError = /^MEDIA_/.test(response.message || '');
        const moderationUnavailable = response.statusCode === 503 && response.message === 'MODERATION_UNAVAILABLE';
        const imageUploadRequired = response.statusCode === 422 && response.message === 'IMAGE_UPLOAD_REQUIRED';
        message.error({ content: accountProtectionMessage(response, t) || contentLimitMessage(response.errorCode || response.message, t) || (moderationUnavailable
          ? t('brand:moderationUnavailable', { defaultValue: 'Could not check your comment. Your draft is still here. Try again later.' })
          : imageUploadRequired ? t('brand:imageUploadRequired', { defaultValue: 'This image is unavailable. Remove it and upload it again. Your text is still here.' })
          : mediaError
          ? t(response.message === 'MEDIA_EDIT_CONFLICT' ? 'media:editConflict' : 'media:saveError', {
            defaultValue: response.message === 'MEDIA_EDIT_CONFLICT'
              ? 'Recordings changed in another tab. Review the latest recordings before sending. Your text and new recordings are preserved.'
              : 'Could not save these recordings. Your draft is still here. Try again later.' })
          : linkBlocked
          ? t('brand:linkBlockedSave', { defaultValue: 'Remove the blocked link before sending. Your text and images are still here.' })
          : linkCheckUnavailable
            ? t('brand:linkCheckUnavailableSave', { defaultValue: 'Could not check the links. Try again later. Your text and images are still here.' })
          : linkLimitExceeded
            ? t('brand:linkLimitSave', { defaultValue: 'A comment can contain up to 12 links. Remove extra links and try again.' })
          : quotesUnavailable
          ? t('brand:quoteNotEnabled', { defaultValue: 'Reposts are not available yet. Your text and images are still here. Remove the quote to save only your own comment.' })
          : t('brand:saveError', { defaultValue: 'Could not save your changes. Please try again.' })) });
        return;
      }
      clearStoredOpinion(draft, userId, params.id);
      if (draft.handoff) completeGuestHandoff(draft.handoff.id);
      clearQuoteDraft(attachment.draft);
      void clearDrawings(drawingsScope).catch(() => {});
      await media.clear();
      clearDraftIdentity(userId, params.id);
      if (!mounted.current || version !== pageVersion.current) return;
      leaving = true;
      signalInApp('publication', { storyId: Number(storyId), reactionId: Number(response.id) });
      navigate(url(opinionPath(response.id, location.state?.profileUsername)), { state: { update: true } });
    } catch (error) {
      if (mounted.current && version === pageVersion.current) {
        message.error({ content: t('brand:saveError', { defaultValue: 'Could not save your changes. Please try again.' }) });
      }
    } finally {
      if (mounted.current && version === pageVersion.current && !leaving) {
        pending.current = false;
        setLoading(false);
      }
    }
  }

  const reviewRecordings = async () => {
    if (busy || pending.current) return;
    pending.current = true;
    setLoading('media-conflict');
    const version = pageVersion.current;
    try {
      const latest = await storyComment({ id: Number(params.id) });
      if (!mounted.current || version !== pageVersion.current) return;
      if (!latest.success || Number(latest.story?.id) !== Number(params.id)
        || (latest.user?.id && latest.user.id !== userId)) throw new Error('Opinion unavailable');
      const latestMedia = Array.isArray(latest.media) ? latest.media : [];
      const latestIds = new Set(latestMedia.map(item => item.id));
      const removedKeys = new Set((media.baseline || []).filter(id => !latestIds.has(id)));
      media.items.forEach(item => { if (removedKeys.has(item.id) && item.localId) removedKeys.add(item.localId); });
      await media.rebase(latestMedia);
      if (mounted.current && version === pageVersion.current) {
        // Explicit conflict recovery replaces the old server attachment set.
        // Remove only its obsolete markers; retain author text and new takes.
        if (editor && !editor.isDestroyed && editor.state?.doc) {
          const obsolete = [];
          editor.state.doc.descendants((node, pos) => {
            if (node.type.name === 'recordedMedia' && removedKeys.has(node.attrs.mediaKey)) obsolete.push({ pos, size: node.nodeSize });
          });
          if (obsolete.length) {
            const tr = editor.state.tr.setMeta(MEDIA_SYNC, true).setMeta('addToHistory', false);
            obsolete.reverse().forEach(({ pos, size }) => tr.delete(pos, pos + size));
            editor.view.dispatch(tr);
          }
        }
        setMediaConflict(false);
      }
    } catch {
      if (mounted.current && version === pageVersion.current) message.error({ content: t('media:saveError', {
        defaultValue: 'Could not save these recordings. Your draft is still here. Try again later.' }) });
    } finally {
      if (mounted.current && version === pageVersion.current) { pending.current = false; setLoading(false); }
    }
  };

  const finishMediaRemoval = confirmed => {
    if (!mediaRemoval || mediaRemoval.draftId !== draft.id) return;
    if (confirmed) mediaRemoval.confirm();
    else mediaRemoval.cancel();
    setMediaRemoval(null);
    if (editor && !editor.isDestroyed) {
      editor.setEditable(true, false);
      editor.view.focus();
    }
  };

  const removalCount = (mediaRemoval?.keys?.length || 0) + (mediaRemoval?.images?.length || 0);
  const removalKind = mediaRemoval?.images?.length ? 'Image' : media.items.find(item =>
    mediaRemoval?.keys?.includes(item.localId) || mediaRemoval?.keys?.includes(item.id))?.kind === 'video' ? 'Video' : 'Audio';
  const removalQuestion = `brand:remove${removalCount === 1 ? removalKind : 'Attachments'}Question`;

  const addRecording = value => {
    const item = media.add(value, undefined, { autoUpload: true });
    if (item && editor && !editor.isDestroyed) {
      // Insert at the preserved text caret, not after all comment content.
      editor.commands.insertRecordedMedia(item.localId);
    }
    setRecording(null);
  };

  const recordingPanel = recording && <MediaRecorderPanel kind={recording.kind} enabled={caps[recording.kind]?.enabled}
    backActionRef={recordingBack}
    onAdd={addRecording}
    onRecover={value => media.add(value)}
    onCancel={() => setRecording(null)} />;

  return (
    <>
    {!guest && <input ref={imageInput} type="file" hidden accept={IMAGE_PICKER_ACCEPT} onChange={imagePicked}
      onCancel={() => { imageRequest.current = null; }} />}
    <div inert={drawing || imageMenu ? '' : undefined} aria-hidden={drawing || imageMenu ? true : undefined}
      className={`app-safe-layout comment-editor-container${keyboardVisible ? ' comment-editor-container--keyboard' : ''}${recording?.kind === 'video' ? ' comment-editor-container--recording-video' : ''}`}>
      {
        loading && <BrandLoader className="editor-save-loading" label={t(loading === 'save' ? 'reaction.checking' : 'brand:loading')} />
      }
      <div className="author-info">
        <StoryTitle
          reactionId={reactionId}
          storyId={storyId ?? Number(params.id)}
          title={storyTitle}>
        </StoryTitle>
        {guest ? <div className="post-user guest-editor-author" aria-label={t('brand:guestAuthor', { defaultValue: 'Your opinion' })}>
          <span className="guest-editor-avatar"><UserImage /></span>
          <span className="post_user_name">{t('brand:guestAuthor', { defaultValue: 'Your opinion' })}</span>
          <UserReactionScope scope={addScope} interactive={false} />
        </div> : <PostUser author={storageUser} scope={addScope} storyId={storyId} />}
      </div>

      <div className={`editor-wrapper${hasQuotePlacement(editor?.state?.doc) ? ' editor-wrapper--with-quote' : ''}${media.items.length || recording ? ' editor-wrapper--with-media' : ''}`}>
        {initializing && <BrandLoader compact label={t('brand:loading')} />}
        {loadError && !busy && <LoadFailure onRetry={() => setLoadAttempt((attempt) => attempt + 1)} />}
        {guestStorageError && <p className="editor-media-notice" role="alert">{t('brand:guestDraftStorageError', {
          defaultValue: 'Your draft could not be kept on this device. Shorten it or copy the text before leaving this page.' })}</p>}
        <MediaDraftContext.Provider value={{ media, caps, removingKeys: mediaRemoval?.keys || [], disabled: busy || Boolean(mediaRemoval) }}>
          <QuoteEditorContext.Provider value={{ attachment, disabled: busy || Boolean(mediaRemoval) || showControl }}>
            <DrawingContext.Provider value={{ disabled: saveBlocked, onEdit: openDrawing }}>
              <EditorContent editor={editor} className="editor-content" />
            </DrawingContext.Provider>
          </QuoteEditorContext.Provider>
        </MediaDraftContext.Provider>
        {mediaConflict && <div className="editor-load-error" role="alert">
          <p>{t('media:editConflict', { defaultValue: 'Recordings changed in another tab. Review the latest recordings before sending. Your text and new recordings are preserved.' })}</p>
          <Button disabled={busy} onClick={reviewRecordings}>{t('media:reviewRecordings', { defaultValue: 'Review latest recordings' })}</Button>
        </div>}
        {media.storageError && <p className="editor-media-notice" role="alert">{t('media:draftStorageError', {
          defaultValue: 'This browser could not save your recordings locally. Keep this page open until your comment is sent.' })}</p>}

      </div>

      {recordingPanel}
      {!recording && !showControl && !mediaRemoval && <ContentLimitStatus measurement={contentMeasurement} />}
      <div className="control-panel-editor" onFocusCapture={(event) => {
        // A very narrow safe viewport scrolls this row horizontally. Keep a
        // keyboard-focused control fully visible without scrolling the page.
        const panel = event.currentTarget;
        const bounds = panel.getBoundingClientRect();
        const target = event.target.getBoundingClientRect();
        if (target.right > bounds.right) panel.scrollLeft += target.right - bounds.right;
        else if (target.left < bounds.left) panel.scrollLeft -= bounds.left - target.left;
      }}>
        {mediaRemoval && <ConfirmationPanel className="editor-media-confirm" action="delete"
          question={t(removalQuestion, { defaultValue: 'Delete the selected attachment?' })}
          highlight='.editor-media-node[data-removing="true"], .ProseMirror [data-removing-image="true"]'
          onCancel={() => finishMediaRemoval(false)} onOK={() => finishMediaRemoval(true)}
          confirmLabel={mediaRemoval.images?.length ? t('brand:confirm') : t('media:remove', { defaultValue: 'Remove recording' })}
          cancelLabel={t('common.cancel', { defaultValue: 'Cancel' })} />}
        {!showControl && !mediaRemoval && <div className="editor-toolbar" style={{ '--editor-action-count':
          newMediaToolbar ? 5 + Number(toolbarCaps.audio.enabled) + Number(toolbarCaps.video.enabled) : 7 }}>
          {[
            { key: 'back', label: t('common.back', { defaultValue: 'Back' }), icon: <BackIcon />, onClick: onBack },
            { key: 'delete', label: t('editor.delete', { defaultValue: 'Delete opinion' }), icon: <TrashIcon />,
              onClick: () => setShowControl(true), disabled: busy || Boolean(recording) || !editor || !storyId },
            ...(!newMediaToolbar ? [
              { key: 'undo', label: t('editor.undo', { defaultValue: 'Undo' }), icon: <UndoIcon />,
                onClick: () => editor?.chain().focus().undo().run(), preserveFocus: true,
                disabled: busy || !storyId || !editor?.can().undo() },
              { key: 'redo', label: t('editor.redo', { defaultValue: 'Redo' }), icon: <RedoIcon />,
                onClick: () => editor?.chain().focus().redo().run(), preserveFocus: true,
                disabled: busy || !storyId || !editor?.can().redo() },
            ] : []),
            { key: 'photo', label: t('editor.image', { defaultValue: 'Add image' }), icon: <PhotoIcon />,
              onClick: openImageMenu, preserveFocus: true, disabled: saveBlocked || !editor || !storyId },
            ...['audio', 'video'].filter(kind => toolbarCaps[kind].enabled).map(kind => ({
              key: kind, label: t(kind === 'audio' ? 'media:recordAudio' : 'media:recordVideo',
                { defaultValue: kind === 'audio' ? 'Record audio' : 'Record video' }),
              icon: kind === 'audio' ? <MicIcon /> : <VideoCameraIcon />,
              onClick: () => openRecorder(kind), preserveFocus: true,
              title: !canRecord ? t('media:unsupported', { defaultValue: 'Recording needs a supported browser, HTTPS and device access.' }) : undefined,
              disabled: busy || !storyId || Boolean(recording) || !canRecord || !media.ready
                || !caps[kind].enabled || media.items.filter(item => item.kind === kind).length >= RECORDING_LIMITS[kind].count,
            })),
            { key: 'keyboard', label: t('editor.hide_keyboard', { defaultValue: 'Hide keyboard' }),
              icon: <KeyboardHideIcon />, onClick: hideKeyboard },
            { key: 'send', label: t('editor.send', { defaultValue: 'Send' }),
              icon: <SendIcon />, pending: Boolean(initializing || (!guest && attachment.pending) || busy),
              onClick: sendVoting, disabled: saveBlocked || !editor || !storyId || Boolean(contentMeasurement.error) },
          ].map(action => <Button key={action.key} aria-label={action.label} title={action.title}
            aria-busy={action.pending || undefined}
            aria-haspopup={action.key === 'photo' ? 'menu' : undefined}
            aria-expanded={action.key === 'photo' ? Boolean(imageMenu) : undefined}
            icon={action.icon} onClick={action.onClick} disabled={action.disabled}
            onMouseDown={action.preserveFocus ? event => event.preventDefault() : undefined} />)}
        </div>}
        {showControl && !mediaRemoval &&
          <ConfirmationPanel
            action="delete"
            question={t('brand:removeOpinionQuestion', { defaultValue: 'Delete this opinion and all its content?' })}
            confirmLabel={t('editor.delete', { defaultValue: 'Delete opinion' })}
            onCancel={() => setShowControl(false)}
            onOK={handleDelete}
            busy={Boolean(loading)}
            disabledOk={busy}
          />
        }
      </div>
      {!keyboardVisible &&
        <div className="editor-voting">
          <Voting onChange={value => {
            if (!busy && !pending.current) setAddScope(value);
          }} onChangeComplete={changeVotingComplete} scope={addScope} />
        </div>
      }
      {imageMenu?.draftId === draft.id && <ImageMenu anchor={imageMenu.anchor} onClose={() => setImageMenu(null)}
        onDraw={() => openDrawing()} onUpload={handleInsertImage} />}
      {drawing && <DrawingDialog key={drawing.scope + (drawing.id || '')} session={drawing} onAdd={addDrawing} onClose={() => setDrawing(null)} />}
    </div>
    </>
  );
};

export default ReactionEditor;
